import { LitElement, html, css } from 'lit';

type Mode = 'auto' | 'light' | 'dark';
const NEXT: Record<Mode, Mode> = { auto: 'light', light: 'dark', dark: 'auto' };
const ICON: Record<Mode, string> = { auto: '◐', light: '☀', dark: '☾' };

/**
 * <theme-toggle> — cycles auto → light → dark, persists to localStorage, and
 * sets `data-theme` on <html> (styles.css reads it). First custom element;
 * copy this shape for the rest. CSS custom properties pierce shadow DOM, so the
 * button restyles with the page theme for free.
 */
export class ThemeToggle extends LitElement {
  static properties = { mode: { state: true } };
  declare mode: Mode;

  static styles = css`
    :host {
      display: inline-block;
    }
    button {
      font: inherit;
      font-size: 0.8rem;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.2rem 0.8rem;
      border: none;
      border-left: 1px solid var(--line, #2a2e42);
      border-radius: 0;
      background: transparent;
      color: var(--fg-dim, #a9b1d6);
      line-height: 1.6;
      transition: color 0.12s, background 0.12s;
    }
    button:hover {
      color: var(--accent, #7aa2f7);
      background: var(--panel-hi, #292e42);
    }
    .icon {
      font-size: 0.9rem;
    }
    .label {
      text-transform: lowercase;
      letter-spacing: 0.02em;
    }
  `;

  constructor() {
    super();
    this.mode = 'auto';
  }

  connectedCallback(): void {
    super.connectedCallback();
    const saved = localStorage.getItem('theme');
    if (saved === 'light' || saved === 'dark' || saved === 'auto') this.mode = saved;
    this.#apply();
  }

  #cycle = (): void => {
    this.mode = NEXT[this.mode];
    localStorage.setItem('theme', this.mode);
    this.#apply();
  };

  #apply(): void {
    const root = document.documentElement;
    if (this.mode === 'auto') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', this.mode);
  }

  render() {
    return html`
      <button @click=${this.#cycle} aria-label=${`Theme: ${this.mode}. Click to change.`}>
        <span class="icon" aria-hidden="true">${ICON[this.mode]}</span>
        <span class="label">${this.mode}</span>
      </button>
    `;
  }
}

customElements.define('theme-toggle', ThemeToggle);
